import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Sports.css";

interface SportItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Sports: React.FC = () => {
  const [data, setData] = useState<SportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/sports`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching sports:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: SportItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: SportItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Sports",
    de: "Sportarten",
    ta: "விளையாட்டுகள்",
    si: "ක්‍රීඩා",
    hi: "खेल",
    ar: "رياضات",
    tr: "Sporlar",
    ru: "Виды спорта",
    zh: "体育项目",
    fr: "Sports",
    es: "Deportes",
  };

  const subtitleMap = {
    en: "Sports in German",
    de: "Sportarten auf Deutsch",
    ta: "ஜெர்மனில் விளையாட்டுகள்",
    si: "ජර්මන් භාෂාවේ ක්‍රීඩා",
    hi: "जर्मन में खेल",
    ar: "الرياضات بالألمانية",
    tr: "Almanca'da Sporlar",
    ru: "Виды спорта на немецком",
    zh: "德语体育",
    fr: "Sports en allemand",
    es: "Deportes en alemán",
  };

  return (
    <div className="sports-page-container">
      <div className="sports-page-header">
        <div>
          <h1 className="sports-page-title">{titleMap["de"]}</h1>
          <p className="sports-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="sports-page-loading">Loading...</div>
      ) : (
        <div className="sports-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`sports-page-card ${
                  isPlaying ? "sports-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="sports-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="sports-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="sports-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="sports-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="sports-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Sports;
